import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import {
  resetPasswordWithTokenSchema,
  ResetPasswordWithTokenFormData,
} from "../../schemas/auth.schema";
import apiClient from "../../api/apiClient";
import Input from "../form/Input";
import Button from "../form/Button";
import { Key } from "heroicons-react";

interface ResetPasswordFormProps {
  token: string;
}

export default function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ResetPasswordWithTokenFormData>({
    resolver: zodResolver(resetPasswordWithTokenSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: ResetPasswordWithTokenFormData) => {
    try {
      await apiClient.post(`/api/v1/auth/recover/${token}`, {
        password: data.password,
      });

      toast.success(
        t(
          "login.passwordResetSuccess",
          "Password updated successfully! You can now log in."
        )
      );

      reset();
      navigate("/login", { replace: true });
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        t(
          "login.passwordResetError",
          "Password reset failed. The link may have expired or is invalid."
        );
      toast.error(errorMsg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-5">
      {/* Header */}
      <div className="text-center">
        <Link to="/" className="inline-block">
          <img
            src="./images/bluelogo-new.png"
            alt="Logo"
            className="mx-auto mb-3 max-w-[130px] md:max-w-[150px]"
          />
        </Link>

        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#EBF7F9] text-[#037897] mb-3">
          <Key className="w-6 h-6" />
        </div>

        <h2 className="mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]">
          {t("login.resetTitle", "Set Your New Password")}
        </h2>

        <p className="text-gray-7 text-[#78909C] font-gilroy text-[15px] md:text-[17px]">
          {t(
            "login.resetSubtitle",
            "Enter a new secure password to access your account."
          )}
        </p>
      </div>

      {/* New Password */}
      <Input
        id="newPassword"
        label={t("login.newPasswordLabel", "New Password")}
        type="password"
        placeholder={t("login.newPasswordPlaceholder", "Enter new password")}
        autoComplete="new-password"
        showPasswordToggle
        error={errors.password?.message}
        {...register("password")}
      />

      {/* Confirm Password */}
      <Input
        id="confirmPassword"
        label={t("login.confirmPasswordLabel", "Confirm New Password")}
        type="password"
        placeholder={t("login.confirmPasswordPlaceholder", "Confirm new password")}
        autoComplete="new-password"
        showPasswordToggle
        error={errors.confirmPassword?.message}
        {...register("confirmPassword")}
      />

      <p className="text-xs text-[#78909C]">
        {t(
          "register.passwordHint",
          "Password must be 8-20 characters and contain at least 1 special character"
        )}
      </p>

      {/* Submit Button */}
      <Button
        isLoading={isSubmitting}
        loadingText={t("login.updatingPassword", "Updating password...")}
        className="!mt-2"
      >
        {t("login.updatePasswordBtn", "Set New Password")}
      </Button>

      {/* Cancel Link */}
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="text-sm text-[#78909C] hover:text-[#037897] transition-colors"
        >
          {t("login.cancelReset", "Cancel and back to login")}
        </button>
      </div>
    </form>
  );
}
